/**
 * Polygraf v2 Protocol
 *
 * Types and conversion functions for the v2 transcript HTTP protocol.
 * The v2 protocol uses flat text with character-offset formatting,
 * which must be converted to the paragraph-based DocumentState model.
 */
/**
 * URL for a v2 long-poll: `rev` is the version the viewer holds. `epoch` names
 * the version sequence that version belongs to; versions restart under a new
 * epoch (the Writer disconnected or idled out), so without it the server cannot
 * tell "no change" from "a new lecture whose version is still below mine".
 * Omit it for a source that never sent one — the server then compares versions
 * only, as it did before epochs.
 */
export function buildLongPollUrl(base, rev, epoch) {
    const separator = base.includes('?') ? '&' : '?';
    const epochPart = epoch === undefined ? '' : `&epoch=${epoch}`;
    return `${base}${separator}rev=${rev}${epochPart}`;
}
// ============================================================================
// V2 Response Detection
// ============================================================================
/** Check if a parsed JSON object looks like a v2 initial response */
export function isV2InitialResponse(data) {
    if (typeof data !== 'object' || data === null)
        return false;
    const obj = data;
    return (typeof obj.text === 'string' &&
        typeof obj.version === 'number' &&
        Array.isArray(obj.inline_formats));
}
/** Check if a parsed JSON object looks like a v2 update response */
export function isV2UpdateResponse(data) {
    if (typeof data !== 'object' || data === null)
        return false;
    const obj = data;
    return (typeof obj.version === 'number' && typeof obj.clear === 'boolean' && Array.isArray(obj.updates));
}
/** Heading format types and their corresponding level */
const HEADING_MAP = {
    head1: 1,
    head2: 2,
    head3: 3,
};
/** Format types that map to TextSegment boolean fields */
const SEGMENT_FORMAT_KEYS = ['bold', 'italic', 'underline', 'subscript', 'superscript'];
/**
 * Convert a v2 initial response to a DocumentState.
 *
 * Splits the flat text into paragraphs by newline, then maps
 * character-offset inline_formats to paragraph-local TextSegments.
 */
export function v2ToDocumentState(state) {
    const paragraphRanges = splitIntoParagraphRanges(state.flatText);
    const paragraphs = paragraphRanges.map((range, idx) => {
        const overlapping = collectOverlappingFormats(state.inlineFormats, range.globalStart, range.globalEnd);
        const headingLevel = detectHeadingLevel(overlapping);
        const segments = buildSegments(range.text, overlapping, range.globalStart);
        const paragraph = {
            id: `v2-p-${idx}`,
            segments,
        };
        if (headingLevel) {
            return { ...paragraph, headingLevel };
        }
        return paragraph;
    });
    return {
        id: 'local',
        title: state.title,
        paragraphs,
        version: state.version,
        lastModified: Date.now(),
    };
}
/**
 * Create a V2ClientState from an initial v2 response.
 */
export function v2InitialToClientState(response) {
    return {
        flatText: response.text,
        inlineFormats: response.inline_formats,
        version: response.version,
        title: response.title,
    };
}
// ============================================================================
// V2 Update Application
// ============================================================================
/**
 * Apply a v2 update response to the current client state.
 * Returns a new state (immutable).
 */
export function applyV2Updates(state, response) {
    if (response.clear) {
        return {
            flatText: '',
            inlineFormats: [],
            version: response.version,
            title: response.title,
        };
    }
    let flatText = state.flatText;
    let inlineFormats = [...state.inlineFormats.map((f) => ({ ...f, formats: [...f.formats] }))];
    for (const update of response.updates) {
        switch (update.type) {
            case 'remove': {
                flatText = flatText.slice(0, update.start) + flatText.slice(update.end);
                // Drop any formats inside the deleted range before shifting survivors,
                // otherwise formatting from deleted text migrates onto neighbouring chars.
                inlineFormats = removeFormatsInRange(inlineFormats, update.start, update.end);
                inlineFormats = shiftFormats(inlineFormats, update.start, update.start - update.end);
                break;
            }
            case 'insert': {
                const insertText = update.text ?? '';
                flatText = flatText.slice(0, update.start) + insertText + flatText.slice(update.start);
                inlineFormats = shiftFormats(inlineFormats, update.start, insertText.length);
                if (update.inline_formats.length > 0) {
                    inlineFormats = mergeFormats(inlineFormats, update.start, insertText.length, update.inline_formats);
                }
                break;
            }
            case 'replace': {
                const replaceText = update.text ?? '';
                const removedLength = update.end - update.start;
                flatText = flatText.slice(0, update.start) + replaceText + flatText.slice(update.end);
                // Remove formats in the replaced range, then shift
                inlineFormats = removeFormatsInRange(inlineFormats, update.start, update.end);
                inlineFormats = shiftFormats(inlineFormats, update.start, replaceText.length - removedLength);
                if (update.inline_formats.length > 0) {
                    inlineFormats = mergeFormats(inlineFormats, update.start, replaceText.length, update.inline_formats);
                }
                break;
            }
            case 'format': {
                if (update.inline_formats.length === 0) {
                    inlineFormats = removeFormatsInRange(inlineFormats, update.start, update.end);
                }
                else {
                    inlineFormats = setFormatsInRange(inlineFormats, update.start, update.end, update.inline_formats);
                }
                break;
            }
        }
    }
    return {
        flatText,
        inlineFormats,
        version: response.version,
        title: response.title,
    };
}
// ============================================================================
// Format Shifting & Merging Helpers
// ============================================================================
/**
 * Shift all format ranges that start at or after `position` by `delta` characters.
 * Handles negative deltas (text removal) by clamping/removing affected formats.
 */
export function shiftFormats(formats, position, delta) {
    if (delta === 0)
        return formats;
    return formats
        .map((f) => {
        if (f.end <= position) {
            // Entirely before the edit point — unchanged
            return f;
        }
        if (f.start >= position) {
            // Entirely after the edit point — shift both
            const newStart = Math.max(0, f.start + delta);
            const newEnd = Math.max(0, f.end + delta);
            if (newStart >= newEnd)
                return null;
            return { ...f, start: newStart, end: newEnd };
        }
        // Spans the edit point — shift only end
        const newEnd = Math.max(f.start, f.end + delta);
        if (f.start >= newEnd)
            return null;
        return { ...f, end: newEnd };
    })
        .filter((f) => f !== null);
}
/**
 * Add a new format range at the given position with the given length.
 */
export function mergeFormats(formats, start, length, newFormats) {
    // An empty range formats nothing; storing it only feeds phantom segment
    // boundaries into buildSegments (MU N3b).
    if (length <= 0)
        return formats;
    return [...formats, { start, end: start + length, formats: [...newFormats] }];
}
/**
 * Remove all format ranges that overlap with [start, end).
 * Formats partially overlapping are clipped.
 */
export function removeFormatsInRange(formats, start, end) {
    const result = [];
    for (const f of formats) {
        if (f.end <= start || f.start >= end) {
            // No overlap — keep as is
            result.push(f);
        }
        else if (f.start < start && f.end > end) {
            // Format spans the entire range — split into two
            result.push({ ...f, end: start, formats: [...f.formats] });
            result.push({ ...f, start: end, formats: [...f.formats] });
        }
        else if (f.start < start) {
            // Overlaps at the end — clip
            result.push({ ...f, end: start, formats: [...f.formats] });
        }
        else if (f.end > end) {
            // Overlaps at the start — clip
            result.push({ ...f, start: end, formats: [...f.formats] });
        }
        // Else: fully contained — remove
    }
    return result;
}
/**
 * Set formats for a range, replacing any existing formats in that range.
 */
export function setFormatsInRange(formats, start, end, newFormats) {
    // A collapsed or inverted selection formats nothing — and must not touch
    // what is already there: removeFormatsInRange would split a range around
    // the caret and leave the same text as two segments (CodeRabbit on #634).
    if (start >= end)
        return formats;
    const cleaned = removeFormatsInRange(formats, start, end);
    return [...cleaned, { start, end, formats: [...newFormats] }];
}
// ============================================================================
// Internal Helpers
// ============================================================================
/** Split flat text into paragraph ranges with global offset tracking */
function splitIntoParagraphRanges(text) {
    const ranges = [];
    let offset = 0;
    const lines = text.split('\n');
    for (const line of lines) {
        ranges.push({
            text: line,
            globalStart: offset,
            globalEnd: offset + line.length,
        });
        offset += line.length + 1; // +1 for the newline
    }
    return ranges;
}
/** Collect inline formats that overlap with a paragraph's global range */
function collectOverlappingFormats(formats, paraStart, paraEnd) {
    return formats.filter((f) => f.start < paraEnd && f.end > paraStart);
}
/** Detect if any format in the set contains a heading type */
function detectHeadingLevel(formats) {
    for (const f of formats) {
        for (const fmt of f.formats) {
            if (fmt in HEADING_MAP) {
                return HEADING_MAP[fmt];
            }
        }
    }
    return undefined;
}
/**
 * Build TextSegments from a paragraph's text and overlapping formats.
 *
 * Uses an interval-merge approach: collect all format boundary points,
 * sort them, then walk through creating segments with accumulated formatting.
 */
function buildSegments(paragraphText, overlappingFormats, paragraphGlobalStart) {
    if (paragraphText.length === 0) {
        return [{ text: '' }];
    }
    // Only ranges that carry an inline format AND cover at least one character.
    // An empty range (collapsed caret) adds a segment boundary with no
    // formatting, so the same text rendered as one span before a push and two
    // after it (MU N3b). The filter lives here, not in collectOverlappingFormats,
    // because heading detection must still see a zero-length heading range —
    // "mark this line as H1" with the caret in the line and nothing selected.
    const segmentFormats = overlappingFormats.filter((f) => f.start < f.end &&
        f.formats.some((fmt) => SEGMENT_FORMAT_KEYS.includes(fmt)));
    if (segmentFormats.length === 0) {
        return [{ text: paragraphText }];
    }
    // Collect all boundary points (local to paragraph)
    const boundaries = new Set();
    boundaries.add(0);
    boundaries.add(paragraphText.length);
    for (const f of segmentFormats) {
        const localStart = Math.max(0, f.start - paragraphGlobalStart);
        const localEnd = Math.min(paragraphText.length, f.end - paragraphGlobalStart);
        if (localStart > 0 && localStart < paragraphText.length)
            boundaries.add(localStart);
        if (localEnd > 0 && localEnd < paragraphText.length)
            boundaries.add(localEnd);
    }
    const sortedBoundaries = Array.from(boundaries).sort((a, b) => a - b);
    // Build segments between consecutive boundaries
    const segments = [];
    for (let i = 0; i < sortedBoundaries.length - 1; i++) {
        const segStart = sortedBoundaries[i];
        const segEnd = sortedBoundaries[i + 1];
        const text = paragraphText.slice(segStart, segEnd);
        if (text.length === 0)
            continue;
        // Determine active formats at this position (using global offsets)
        const globalSegStart = paragraphGlobalStart + segStart;
        const globalSegEnd = paragraphGlobalStart + segEnd;
        const activeFormats = new Set();
        for (const f of segmentFormats) {
            if (f.start < globalSegEnd && f.end > globalSegStart) {
                for (const fmt of f.formats) {
                    activeFormats.add(fmt);
                }
            }
        }
        const segment = { text };
        if (activeFormats.has('bold'))
            segment.bold = true;
        if (activeFormats.has('italic'))
            segment.italic = true;
        if (activeFormats.has('underline'))
            segment.underline = true;
        if (activeFormats.has('subscript'))
            segment.subscript = true;
        if (activeFormats.has('superscript'))
            segment.superscript = true;
        // Fold into the previous segment when the formatting is identical. The
        // Writer sends one inline_format range per push, so a passage that stays
        // bold arrives as N abutting ranges; without this, the tail paragraph
        // grew one segment per push for text that never changed, and the viewer
        // (which keys segments by index) rebuilt it every time. MU N3b.
        const previous = segments[segments.length - 1];
        if (previous && sameFormatting(previous, segment)) {
            segments[segments.length - 1] = { ...previous, text: previous.text + text };
        }
        else {
            segments.push(segment);
        }
    }
    return segments.length > 0 ? segments : [{ text: paragraphText }];
}
/** True when two segments differ only in their text. */
function sameFormatting(a, b) {
    return (a.bold === b.bold &&
        a.italic === b.italic &&
        a.underline === b.underline &&
        a.subscript === b.subscript &&
        a.superscript === b.superscript &&
        a.color === b.color &&
        a.backgroundColor === b.backgroundColor);
}
//# sourceMappingURL=v2-protocol.js.map